import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { Store } from '@ngrx/store'
import { environment } from '../../../../environments/environment'
import { PaginationComponent } from '../../template/pagination/pagination.component'
import { CapitalizePipe } from '../../../pipes/capitalize.pipe'
import { CommonModule, NgFor, NgIf } from '@angular/common'
import { FormsModule } from '@angular/forms'
import {
  loadAssignedTo,
  upsertValidUpto,
  deleteAssignedTo,
  createAssignedTo,
  loadRoleById,
  loadRoleByIdSuccess
} from '../../../store/role-permission/role-permission.actions'
import { selectAssignedTo } from '../../../store/role-permission/role-permission.reducer'
import { combineLatest, filter, map, Observable, of, Subject, take, takeUntil, tap } from 'rxjs'
import { eps } from '../../../services/url.service'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { loadEmployees } from '../../../store/user/user.actions'
import { selectUsers } from '../../../store/user/user.reducer'
import { User } from '../../../store/auth/auth.state'
import { selectRoleDetail } from '../../../store/role-permission/role-permission.selectors'
import { Role } from '../../../store/role-permission/role-permission.state'
import { Actions, ofType } from '@ngrx/effects'
import { LoaderComponent } from '../../../shared/components/loader/loader.component'

interface ExtendedUser extends User {
  validUpto: string | null
}

@Component({
  selector: 'app-details',
  imports: [
    NgIf,
    NgFor,
    RouterModule,
    FormsModule,
    CommonModule,
    CapitalizePipe,
    PaginationComponent,
    LoaderComponent
  ],
  templateUrl: './details.component.html',
  styleUrl: './details.component.scss'
})
export class PermissionSetDetailsComponent implements OnInit, OnDestroy {
  private store = inject(Store)
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private modalService = inject(NgbModal)
  eps = eps
  roleId!: string
  destroy$ = new Subject<void>()
  assignedTo$ = this.store.select(selectAssignedTo)
  allUsers$ = this.store.select(selectUsers)
  unassignedUsers$!: Observable<ExtendedUser[]>

  idForValidUptoUpdate = ''
  TempValidUpto!: Date | null
  selectedUsers: ExtendedUser[] = []

  totalCount = 0
  currentPage = 1
  pageLimit = 10
  search = ''
  order = 'desc'
  pageLastRecord = 0
  defaultImg = '../../../../assets/img/user.jpg'
  apiEndpoint = environment.apiBaseUrl
  role$: Observable<Role | undefined> = of(undefined) // Initialize with a default value
  @ViewChild('assignUsersModal') assignUsersModal!: TemplateRef<any>
  searchText = ''

  constructor(private actions$: Actions) {
    // const roleId = this.route.snapshot.paramMap.get('id')
    // if (!roleId) this.router.navigate([eps.userRole.rolePath])
    // this.roleId = Number(roleId)
    // this.store.dispatch(loadRoleById({ id: this.roleId }))
    // this.role$ = this.store.select(selectRoleDetail)
  }

  ngOnInit(): void {
    const roleId = this.route.snapshot.paramMap.get('id')
    if (!roleId) this.router.navigate([eps.userRole.rolePath])
    if (roleId) {
      this.roleId = roleId
    }
    this.store.dispatch(loadRoleById({ id: this.roleId }))
    this.role$ = this.store.select(selectRoleDetail)

    this.store.dispatch(loadAssignedTo({ roleId: this.roleId }))
    // this.store.dispatch(loadEmployees({}))

    this.unassignedUsers$ = combineLatest([this.allUsers$, this.assignedTo$]).pipe(
      map(([allUsers, assignedUsers]) => {
        const assignedList = Array.isArray(assignedUsers) ? assignedUsers : []
        const assignedIds = new Set(assignedList.map(u => u.user.id))

        const validUptoDefault = this.getFormattedDate(
          new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        )

        return allUsers
          .filter(user => !assignedIds.has(user.id))
          .map(user => ({
            ...user,
            validUpto: validUptoDefault
          }))
      })
      // map(([allUsers, assignedUsers]) => {
      //   const assignedList = assignedUsers ?? []
      //   const assignedIds = new Set(assignedList?.map(u => u.user.id))

      //   //Create a date 30 days ahead of now
      //   const validUptoDefault = this.getFormattedDate(
      //     new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      //   )
      //   return allUsers
      //     .filter(user => !assignedIds.has(user.id))
      //     .map(user => ({
      //       ...user,
      //       validUpto: validUptoDefault
      //     }))
      // })
    )

    this.assignedTo$.pipe(takeUntil(this.destroy$)).subscribe(d => {
      this.totalCount = d?.length ?? 0
      this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.totalCount)
    })

    // 👇 Wait for loadRoleByIdSuccess before selecting role
    this.actions$
      .pipe(
        ofType(loadRoleByIdSuccess),
        take(1),
        tap(({ role }) => {
          if (role?.businessId) {
            this.store.dispatch(
              loadEmployees({
                params: new URLSearchParams({ businessId: role.businessId }).toString()
              })
            )
          } else {
            this.store.dispatch(loadEmployees({}))
          }
        })
      )
      .subscribe()
  }
  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  //Left this function from past to get the skip value for when setting pagination
  getAssignedEmployeeList() {
    //const skip = this.currentPage * this.pageLimit - this.pageLimit
  }

  pageChanged(page: number) {
    this.currentPage = page
    this.getAssignedEmployeeList()
  }

  getFormattedDate(date: string | null) {
    if (!date) return null
    return new Date(date).toISOString().slice(0, 10) // Just YYYY-MM-DD
  }

  // getFormattedDate(date: string | Date | null): string | null {
  //   if (!date) return null

  //   const d = typeof date === 'string' ? new Date(date) : date

  //   return d.toLocaleString('en-IN', {
  //     timeZone: 'Asia/Kolkata',
  //     year: 'numeric',
  //     month: 'short',
  //     day: 'numeric',
  //     hour: 'numeric',
  //     minute: '2-digit',
  //     hour12: true
  //   })
  // }

  //---Editing Assigned Users---
  editValidUpto(id: string) {
    this.idForValidUptoUpdate = id
  }

  saveValidUpto() {
    this.store.dispatch(
      upsertValidUpto({ id: this.idForValidUptoUpdate, validUpto: this.TempValidUpto })
    )
    this.idForValidUptoUpdate = ''
    this.TempValidUpto = null
  }

  deleteAssignment(id: string) {
    this.store.dispatch(deleteAssignedTo({ id }))
  }
  //---Editing Assigned Users Ends---

  //---Assigning Users---
  openModel(modal: unknown) {
    this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'lg'
    })
  }

  onClose() {
    this.searchText = '' // Reset search box
    this.selectedUsers = [] // Clear selected users
    this.getAssignedEmployeeList()
    this.modalService.dismissAll()
  }

  toggleSelection(user: ExtendedUser) {
    const index = this.selectedUsers.findIndex(u => u.id === user.id)
    if (index === -1) this.selectedUsers.push(user)
    else this.selectedUsers.splice(index, 1)
  }

  saveAssignedUsers() {
    const userData = this.selectedUsers.map(u => ({ userId: u.id, validUpto: u.validUpto }))
    this.store.dispatch(
      createAssignedTo({
        userRoleId: this.roleId,
        userData
      })
    )
    this.onClose()
  }

  filteredUsers(users: any[]): any[] {
    if (!this.searchText) {
      return users
    }
    const lowerSearch = this.searchText.toLowerCase()
    return users.filter(user => user.firstName?.toLowerCase().includes(lowerSearch))
  }

  formatForDateTimeLocal(date: string | Date | null): string | null {
    if (!date) return null
    const d = typeof date === 'string' ? new Date(date) : date

    const pad = (n: number) => n.toString().padStart(2, '0')

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  getInitials(firstName: string = '', lastName: string = ''): string {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  //---Assigning Users Ends---
}
